import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Portal,
} from '@mui/material'
import { find, first, isEmpty, isNil, isNull, map } from 'lodash'

import { Dictionaries, Tyres } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import SelectableCard from '../components/SelectableCard'
import TyresTable from '../components/TyresTable'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowLeft,
  ArrowRight,
  CancelIcon,
  CheckIcon,
  SaveIcon,
  TiresIcon,
  TireSummerIcon,
  TireWinterIcon,
} from '../icons'

function ControlsEditTyres() {
  const { controlId, portalRef, onCancelClick } = useOutletContext()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const [isBtnLoading, setIsBtnLoading] = useState(false)
  const [tyresType, setTyresType] = useState(null)
  const [tyresWearDictionary, setTyresWearDictionary] = useState({})
  const [values, setValues] = useState([])
  const { t } = useTranslation()
  const { front, rear } = find(values, ['type', 'summer']) || {}
  const isAllSeasonsTyres = (front?.isAllSeasons && rear?.isAllSeasons) || false

  useEffect(() => {
    Tyres.getTyres(axiosPrivate, controlId)
      .then(({ data }) => {
        setValues(data)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))

    Dictionaries.getTyresWearDictionary(axiosPrivate)
      .then(({ data }) => {
        setTyresWearDictionary(data)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, controlId])

  useEffect(() => {
    if (isNull(tyresType)) {
      const firstTyresSet = first(values) || {}
      setTyresType(firstTyresSet.type || null)
    }
  }, [values, tyresType])

  const handleTyreClick = (type) => (e) => {
    e.preventDefault()
    setTyresType(type)
  }

  const handleAllSeasonsChange = (e) => {
    const { checked } = e.currentTarget

    setValues((prevTyres) => {
      if (isEmpty(prevTyres)) {
        return [
          {
            front: { isAllSeasons: checked },
            rear: { isAllSeasons: checked },
            type: 'summer',
          },
        ]
      }

      return map(prevTyres, (tyreSet) =>
        tyreSet.type === 'summer'
          ? {
              ...tyreSet,
              front: { ...tyreSet.front, isAllSeasons: checked },
              rear: { ...tyreSet.rear, isAllSeasons: checked },
            }
          : tyreSet,
      )
    })
  }

  const updateValues = useCallback(
    (axle, type, newValues) => {
      const currentTyresSet = find(values, ['type', type])

      setValues((prevTyres) =>
        currentTyresSet
          ? map(prevTyres, (tyreSet) =>
              tyreSet.type === type
                ? { ...tyreSet, [axle]: newValues }
                : tyreSet,
            )
          : [...prevTyres, { type, [axle]: newValues }],
      )
    },
    [values],
  )

  const saveTyresCondition = (redirect) => () => {
    setIsBtnLoading(true)
    Tyres.saveTyres(axiosPrivate, controlId, values)
      .then(() => {
        if (redirect) {
          navigate(redirect)
        }
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
      .finally(() => {
        setIsBtnLoading(false)
      })
  }

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<TiresIcon />}
          title="Opony"
          subtitle="Wybierz i opisz opony dostarczone z pojazdem"
        />
        <Grid container spacing={5}>
          <Grid item xs={12} md={6}>
            <SelectableCard
              onClick={handleTyreClick('summer')}
              title="Opony letnie / wielosezonowe"
              icon={<TireSummerIcon />}
              selected={tyresType === 'summer'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectableCard
              onClick={handleTyreClick('winter')}
              title="Opony zimowe"
              icon={<TireWinterIcon />}
              selected={tyresType === 'winter'}
            />
          </Grid>
        </Grid>
        {tyresType === 'summer' && (
          <Box sx={{ mt: 5, mx: 4 }}>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isAllSeasons"
                    value={isAllSeasonsTyres}
                    checked={isAllSeasonsTyres}
                    onChange={handleAllSeasonsChange}
                  />
                }
                label="Czy są to opony wielosezonowe?"
              />
            </FormControl>
          </Box>
        )}
        {!isNil(tyresType) && (
          <Box sx={{ mt: 5 }}>
            <Paper elevation={0} sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TyresTable
                    head="Oś przednia"
                    axle="front"
                    tyresType={tyresType}
                    initialValues={values}
                    tyresWear={tyresWearDictionary}
                    updateValues={updateValues}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TyresTable
                    head="Oś tylna"
                    axle="rear"
                    tyresType={tyresType}
                    initialValues={values}
                    tyresWear={tyresWearDictionary}
                    updateValues={updateValues}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        <Portal container={portalRef.current}>
          <ButtonBox
            buttons={[
              {
                label: 'Wstecz',
                icon: <ArrowLeft fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTyresCondition(
                    `/controls/${controlId}/edit/equipment`,
                  ),
                },
              },
              {
                label: 'Anuluj',
                icon: <CancelIcon />,
                props: {
                  variant: 'outlined',
                  color: 'primary',
                  onClick: onCancelClick,
                },
              },
              {
                label: 'Zapisz',
                icon: <SaveIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTyresCondition(false),
                },
              },
              {
                label: 'Zakończ',
                icon: <CheckIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTyresCondition(`/controls/${controlId}/summary`),
                },
              },
              {
                label: t('nextBtnLabel'),
                icon: <ArrowRight fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTyresCondition(
                    `/controls/${controlId}/edit/technicalCondition`,
                  ),
                },
              },
            ]}
          />
        </Portal>
      </Section>
    </div>
  )
}

export default ControlsEditTyres
